import api from './api.service';

export interface SystemSetting {
  id: number;
  setting_key: string;
  setting_value: string;
  setting_type: string;
  /** Some APIs expose type as data_type */
  data_type?: string;
  category: string;
  description: string;
  is_public: boolean;
  is_encrypted: boolean;
}

export interface CompanySetting {
  id: number;
  company_id: number;
  setting_key: string;
  setting_value: string;
  setting_type: string;
  category: string;
}

export interface DocumentSequence {
  id: number;
  company_id: number | string;
  document_type: string;
  prefix: string;
  current_number: number;
  separator: string;
  padding_length?: number;
  include_year?: boolean;
  include_month?: boolean;
  reset_yearly?: boolean;
  reset_monthly?: boolean;
  last_period_key?: string | null;
  example_output?: string;
  is_active?: boolean;
}

export interface EmailTemplateRow {
  id: string;
  template_name: string;
  template_key: string;
  category: string;
  subject: string;
  body: string;
  is_active: boolean;
  is_system?: boolean;
  company_id?: string | null;
}

class SettingsService {
  async getAllSystemSettings(): Promise<SystemSetting[]> {
    try {
      const response = await api.get('/settings/system');
      console.log('Settings API Response:', response);
      console.log('Settings data property:', response.data);
      
      // response is { status: 'success', data: [...settings] }
      // So we access response.data to get the array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // Handle case where response structure might be different
      if (response.data?.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      console.warn('Unexpected settings response structure:', response);
      return [];
    } catch (error: any) {
      console.error('Error fetching system settings:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  }

  async testSystemEmail(to: string): Promise<void> {
    await api.post('/settings/system/test-email', { to });
  }

  async updateSystemSetting(key: string, value: string): Promise<SystemSetting> {
    try {
      const response = await api.put(`/settings/system/${key}`, { value });
      
      // response is { status: 'success', data: {...setting} }
      if (response.data) {
        return response.data;
      }
      
      // Handle case where response structure might be different
      if (response.data?.data) {
        return response.data.data;
      }
      
      throw new Error('Unexpected response structure from settings API');
    } catch (error: any) {
      console.error(`Error updating system setting ${key}:`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        key,
        value
      });
      throw error;
    }
  }

  private unwrapSettingsList<T>(response: unknown): T[] {
    const r = response as { data?: T[] };
    if (Array.isArray(r?.data)) return r.data;
    return [];
  }

  private unwrapSettingsNestedData<T>(response: unknown): T | undefined {
    const r = response as { data?: T };
    return r?.data;
  }

  async getAllDocumentSequences(companyId: string | number): Promise<DocumentSequence[]> {
    const response = await api.get(`/settings/sequences/${String(companyId)}`);
    return this.unwrapSettingsList<DocumentSequence>(response);
  }

  async upsertDocumentSequence(
    companyId: string | number,
    body: {
      id?: number;
      document_type: string;
      prefix?: string;
      separator?: string;
      current_number?: number;
      padding_length?: number;
      include_year?: boolean;
      include_month?: boolean;
      reset_yearly?: boolean;
      reset_monthly?: boolean;
      is_active?: boolean;
    }
  ): Promise<void> {
    await api.put(`/settings/sequences/${String(companyId)}`, body);
  }

  async generateDocumentNumber(companyId: string | number, documentType: string): Promise<string> {
    const response = await api.post(`/settings/sequences/${String(companyId)}/generate`, { documentType });
    const payload = this.unwrapSettingsNestedData<{ documentNumber?: string }>(response);
    const num = payload?.documentNumber;
    if (!num) {
      throw new Error('Unexpected response from generate document number');
    }
    return num;
  }

  async previewDocumentNumber(companyId: string | number, documentType: string): Promise<string> {
    const response = await api.get(`/settings/sequences/${String(companyId)}/preview`, {
      params: { documentType },
    });
    const payload = this.unwrapSettingsNestedData<{ documentNumber?: string }>(response);
    const num = payload?.documentNumber;
    if (!num) {
      throw new Error('Unexpected response from preview document number');
    }
    return num;
  }

  async getEmailTemplates(): Promise<EmailTemplateRow[]> {
    const response = await api.get('/settings/email-templates');
    return this.unwrapSettingsList<EmailTemplateRow>(response);
  }

  async updateEmailTemplate(
    id: string,
    payload: { subject: string; bodyHtml: string; is_active: boolean }
  ): Promise<void> {
    await api.put(`/settings/email-templates/${encodeURIComponent(id)}`, {
      subject: payload.subject,
      body: payload.bodyHtml,
      is_active: payload.is_active,
    });
  }

  async getDocumentSequenceTypes(): Promise<
    { value: string; label: string; defaultPrefix: string }[]
  > {
    const body = await api.get<{ data?: { value: string; label: string; defaultPrefix: string }[] }>(
      '/settings/sequence-types'
    );
    const d = (body as { data?: { value: string; label: string; defaultPrefix: string }[] }).data;
    return Array.isArray(d) ? d : [];
  }

  async createEmailTemplate(payload: {
    template_name: string;
    template_code: string;
    event_type: string;
    subject: string;
    body: string;
    is_active: boolean;
    company_id?: string | null;
  }): Promise<string> {
    const body = await api.post<{ data?: { id?: string } }>('/settings/email-templates', {
      template_name: payload.template_name,
      template_code: payload.template_code,
      event_type: payload.event_type,
      subject: payload.subject,
      body: payload.body,
      is_active: payload.is_active,
      company_id: payload.company_id,
    });
    return (body as { data?: { id?: string } })?.data?.id ?? '';
  }

  async deleteEmailTemplate(id: string): Promise<void> {
    await api.delete(`/settings/email-templates/${encodeURIComponent(id)}`);
  }

  /**
   * Per-tenant company profile settings (company_settings + companies hydrate).
   * Response shape: { [setting_key]: { value, type, category } }
   */
  async getCompanySettings(): Promise<Record<string, { value: string; type?: string; category?: string }>> {
    const response = await api.get('/settings/company');
    const data =
      (response as any)?.data?.data ??
      (response as any)?.data ??
      response;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, { value: string; type?: string; category?: string }>;
    }
    return {};
  }

  /**
   * Batch upsert company profile keys for the logged-in company.
   * Payload: { [setting_key]: string | { value, type?, category? } }
   */
  async updateCompanySettings(
    settingsData: Record<string, string | { value: string; type?: string; category?: string }>
  ): Promise<Record<string, { value: string; type?: string; category?: string }>> {
    const response = await api.put('/settings/company', settingsData);
    const data =
      (response as any)?.data?.data ??
      (response as any)?.data ??
      response;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, { value: string; type?: string; category?: string }>;
    }
    return {};
  }
}

export const settingsService = new SettingsService();
export default settingsService;

